import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { courseList } from '@/data/courses/courseList'
import { courseIconGlyph } from '@/lib/courseIcon'

interface SearchResult {
  type: 'course' | 'page'
  title: string
  description: string
  href: string
  icon?: string
}

const staticPages: SearchResult[] = [
  { type: 'page', title: 'Playground', description: 'Interactive code editor and visualizations', href: '/playground' },
  { type: 'page', title: 'Glossary', description: 'AI/ML terms and definitions', href: '/glossary' },
  { type: 'page', title: 'Resources', description: 'Curated papers, courses, and tools', href: '/resources' },
  { type: 'page', title: 'Roadmap', description: 'Recommended learning path', href: '/roadmap' },
  { type: 'page', title: 'Profile', description: 'Your progress and achievements', href: '/profile' },
  { type: 'page', title: 'Settings', description: 'Theme, font size, data management', href: '/settings' },
]

const allResults: SearchResult[] = [
  ...courseList.map((c) => ({
    type: 'course' as const,
    title: c.title,
    description: c.description,
    href: `/courses/${c.id}`,
    icon: c.icon,
  })),
  ...staticPages,
]

export function SearchModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const [selectedIndex, setSelectedIndex] = useState(0)

  const results = query
    ? allResults.filter(
        (r) =>
          r.title.toLowerCase().includes(query.toLowerCase()) ||
          r.description.toLowerCase().includes(query.toLowerCase()),
      )
    : allResults.slice(0, 6)

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        setQuery('')
        setSelectedIndex(0)
        inputRef.current?.focus()
      }, 0)
    }
  }, [open])

  const handleSelect = useCallback(
    (result: SearchResult) => {
      navigate(result.href)
      onClose()
    },
    [navigate, onClose],
  )

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex((i) => Math.min(i + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      handleSelect(results[selectedIndex])
    } else if (e.key === 'Escape') {
      onClose()
    }
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] sm:pt-[15vh] px-4 bg-black/50"
      onClick={onClose}
    >
      {/* Modal — centered, responsive */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[560px] bg-[var(--color-surface-0)] border border-[var(--color-border-default)] rounded-[var(--radius-lg)] shadow-[var(--shadow-xl)] overflow-hidden"
      >

          {/* Search input */}
          <div className="flex items-center gap-3 px-4 h-14 border-b border-[var(--color-border-default)]">
            <svg className="w-5 h-5 text-[var(--color-text-muted)] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setSelectedIndex(0) }}
              onKeyDown={handleKeyDown}
              placeholder="Search courses, tools, pages..."
              className="flex-1 text-[15px] bg-transparent text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] outline-none"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            <kbd className="hidden sm:inline-flex px-1.5 py-0.5 text-[11px] font-mono text-[var(--color-text-muted)] bg-[var(--color-surface-2)] rounded border border-[var(--color-border-default)]">
              ESC
            </kbd>
            <button
              onClick={onClose}
              aria-label="Close search"
              className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-2)] rounded-[var(--radius-sm)] transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Results */}
          <div className="max-h-[50vh] overflow-y-auto">
            {query && results.length === 0 && (
              <div className="px-4 py-10 text-center">
                <p className="text-[14px] text-[var(--color-text-muted)]">
                  No results for &ldquo;{query}&rdquo;
                </p>
              </div>
            )}

            {!query && (
              <p className="px-4 pt-3 pb-1 text-[11px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wide">
                Quick links
              </p>
            )}

            {results.map((result, i) => (
              <button
                key={result.href}
                onClick={() => handleSelect(result)}
                onMouseEnter={() => setSelectedIndex(i)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  i === selectedIndex
                    ? 'bg-[var(--color-brand-500)]/8'
                    : ''
                }`}
              >
                <span className="w-9 h-9 rounded-[var(--radius-md)] bg-[var(--color-surface-1)] border border-[var(--color-border-default)] flex items-center justify-center text-[15px] shrink-0">
                  {result.type === 'course' ? courseIconGlyph(result.icon) : (result.icon ?? '📄')}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-medium text-[var(--color-text-primary)] truncate">
                    {result.title}
                  </p>
                  <p className="text-[12px] text-[var(--color-text-muted)] truncate">
                    {result.description}
                  </p>
                </div>
                <span className="text-[11px] font-medium px-2 py-0.5 rounded-[var(--radius-sm)] bg-[var(--color-surface-1)] text-[var(--color-text-muted)] capitalize shrink-0">
                  {result.type}
                </span>
              </button>
            ))}
          </div>

          {/* Footer hint */}
          <div className="px-4 py-2.5 border-t border-[var(--color-border-default)] flex items-center justify-between text-[11px] text-[var(--color-text-muted)]">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <kbd className="px-1 py-0.5 bg-[var(--color-surface-2)] rounded border border-[var(--color-border-default)] text-[10px] font-mono">↑↓</kbd>
                navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1 py-0.5 bg-[var(--color-surface-2)] rounded border border-[var(--color-border-default)] text-[10px] font-mono">↵</kbd>
                open
              </span>
            </div>
            <span>{results.length} result{results.length !== 1 ? 's' : ''}</span>
          </div>

      </div>
    </div>
  )
}
